import * as React from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '../utils';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        primary:
          'bg-orange text-white hover:bg-orange-500 focus-visible:ring-orange shadow-sm',
        secondary:
          'bg-neutral-950 text-white hover:bg-neutral-800 focus-visible:ring-neutral-700',
        outline:
          'border border-neutral-950 text-neutral-950 bg-transparent hover:bg-neutral-950 hover:text-white focus-visible:ring-neutral-700',
        'outline-white':
          'border border-white text-white bg-transparent hover:bg-white hover:text-neutral-950 focus-visible:ring-white',
        ghost:
          'text-neutral-700 hover:bg-neutral-100 focus-visible:ring-neutral-400',
        sky:
          'bg-sky text-white hover:bg-sky-500 focus-visible:ring-sky shadow-sm',
      },
      size: {
        sm:      'h-9 px-4 text-sm rounded-lg',
        default: 'h-11 px-6 text-sm rounded-xl',
        lg:      'h-14 px-6 text-base rounded-xl',
        xl:      'h-20 px-8 text-lg rounded-2xl',
        icon:    'h-10 w-10 rounded-lg',
      },
    },
    defaultVariants: {
      variant: 'primary',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  ),
);
Button.displayName = 'Button';

export { Button, buttonVariants };
